import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { 
  listTopMerchantsForAdmin, 
  TopMerchant, 
  MerchantSortBy 
} from "../api/business";
import { useAuth } from "../context/AuthContext";
import { CompanySelect } from "../components/CompanySelect";
import {
  formatCurrency,
  formatInteger,
} from "../helpers/formatNumber";

const PAGE_SIZE = 100;

// Helper functions for date calculations
const getDateRangeForThisMonth = () => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  
  return {
    start: startOfMonth.toISOString().slice(0, 16), // Format for datetime-local input
    end: endOfMonth.toISOString().slice(0, 16)
  };
};

const getDateRangeForPreviousMonth = () => {
  const now = new Date();
  const startOfPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const endOfPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
  
  return {
    start: startOfPrevMonth.toISOString().slice(0, 16),
    end: endOfPrevMonth.toISOString().slice(0, 16)
  };
};

const getDateRangeForLastThreeMonths = () => {
  const now = new Date();
  const startOfThreeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, 1);
  const endOfCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  
  return {
    start: startOfThreeMonthsAgo.toISOString().slice(0, 16),
    end: endOfCurrentMonth.toISOString().slice(0, 16)
  };
};

const AdminTopMerchants: React.FC = () => {
  const { selectedCompany } = useAuth();
  const [merchants, setMerchants] = useState<TopMerchant[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [total, setTotal] = useState(0);
  const [startDate, setStartDate] = useState<string>(() => getDateRangeForPreviousMonth().start);
  const [endDate, setEndDate] = useState<string>(() => getDateRangeForPreviousMonth().end);
  const [sortBy, setSortBy] = useState<MerchantSortBy>(MerchantSortBy.TotalAssignedCashback);
  const [selectedQuickFilter, setSelectedQuickFilter] = useState<string>("previousMonth");

  const fetchTopMerchants = useCallback(async () => {
    try {
      setLoading(true);

      // Create ISO strings treating input as UTC time
      let fromDateString: string | undefined = undefined;
      let toDateString: string | undefined = undefined;

      if (startDate) {
        // Directly create UTC date by appending 'Z' to the input date string
        fromDateString = startDate + "Z";
      }

      if (endDate) {
        // Directly create UTC date by appending 'Z' to the input date string
        toDateString = endDate + "Z";
      }

      const response = await listTopMerchantsForAdmin({
        pagination: {
          pageNumber,
          pageSize: PAGE_SIZE,
        },
        companyId: selectedCompany?.id,
        from: fromDateString,
        to: toDateString,
        sortBy: sortBy,
      });
      setMerchants(response.data.data);
      setTotal(response.data.total);
      setError(null);
    } catch (err) {
      setError("Failed to fetch top merchants");
      console.error("Error fetching top merchants:", err);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [pageNumber, selectedCompany, startDate, endDate, sortBy]);

  useEffect(() => {
    fetchTopMerchants();
  }, [fetchTopMerchants]);

  const handleNextPage = () => {
    setPageNumber((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  };

  const handleExportCSV = () => {
    // Get top 20 merchants from current data
    const top20Merchants = merchants.slice(0, 20);
    
    if (top20Merchants.length === 0) {
      alert("No data to export");
      return;
    }

    // Create CSV headers
    const headers = [
      "Rank",
      "Merchant Name", 
      "Total Assigned Cashback",
      "Total Transaction Amount",
      "Transaction Count"
    ];

    // Create CSV rows
    const csvRows = top20Merchants.map((merchant, index) => {
      const globalIndex = (pageNumber - 1) * PAGE_SIZE + index;
      return [
        globalIndex + 1, // Rank
        `"${merchant.merchantName}"`, // Wrap in quotes to handle commas in names
        merchant.totalAssignedCashback,
        merchant.totalTransactionAmount,
        merchant.transactionCount
      ].join(",");
    });

    // Combine headers and rows
    const csvContent = [headers.join(","), ...csvRows].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      
      // Generate filename with current date and filter info
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const companyStr = selectedCompany ? `_${selectedCompany.name.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
      const sortStr = `_${sortBy.replace(/([A-Z])/g, '_$1').toLowerCase()}`;
      
      link.setAttribute("download", `top_merchants${companyStr}${sortStr}_${dateStr}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleQuickFilter = (filterType: string) => {
    setSelectedQuickFilter(filterType);
    setPageNumber(1); // Reset to first page when changing filters
    
    switch (filterType) {
      case "thisMonth":
        const thisMonthRange = getDateRangeForThisMonth();
        setStartDate(thisMonthRange.start);
        setEndDate(thisMonthRange.end);
        break;
      case "previousMonth":
        const prevMonthRange = getDateRangeForPreviousMonth();
        setStartDate(prevMonthRange.start);
        setEndDate(prevMonthRange.end);
        break;
      case "lastThreeMonths":
        const threeMonthsRange = getDateRangeForLastThreeMonths();
        setStartDate(threeMonthsRange.start);
        setEndDate(threeMonthsRange.end);
        break;
      case "allTime":
        setStartDate("");
        setEndDate("");
        break;
      default:
        break;
    }
  };

  // Show full page loader only on initial load
  if (initialLoad) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <div className="flex flex-col gap-4 mb-10">
        <h2 className="font-medium">
          Top Merchants -{" "}
          {selectedCompany ? selectedCompany.name : "All Companies"}
        </h2>
        
        {/* Quick Filter Buttons */}
        <div className="flex gap-2 mb-4">
          <Button
            onClick={() => handleQuickFilter("thisMonth")}
            variant={selectedQuickFilter === "thisMonth" ? "primary" : "outline"}
            size="sm"
          >
            This Month
          </Button>
          <Button
            onClick={() => handleQuickFilter("previousMonth")}
            variant={selectedQuickFilter === "previousMonth" ? "primary" : "outline"}
            size="sm"
          >
            Previous Month
          </Button>
          <Button
            onClick={() => handleQuickFilter("lastThreeMonths")}
            variant={selectedQuickFilter === "lastThreeMonths" ? "primary" : "outline"}
            size="sm"
          >
            Last 3 Months
          </Button>
          <Button
            onClick={() => handleQuickFilter("allTime")}
            variant={selectedQuickFilter === "allTime" ? "primary" : "outline"}
            size="sm"
          >
            All Time
          </Button>
        </div>
        
        <div className="flex items-center gap-4 mb-4">
          <div className="w-[300px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company
            </label>
            <CompanySelect />
          </div>
          <div className="w-[250px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date and Time (UTC)
            </label>
            <input
              type="datetime-local"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="w-[250px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date and Time (UTC)
            </label>
            <input
              type="datetime-local"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="w-[250px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort By
            </label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as MerchantSortBy)}
            >
              <option value={MerchantSortBy.TotalAssignedCashback}>
                Total Assigned Cashback
              </option>
              <option value={MerchantSortBy.TotalTransactionAmount}>
                Total Transaction Amount
              </option>
              <option value={MerchantSortBy.TransactionCount}>
                Transaction Count
              </option>
            </select>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="bg-white rounded-lg shadow p-4 min-w-[200px] w-fit">
            <div className="text-gray-500 text-sm">Total Merchants</div>
            <div className="text-2xl font-semibold mt-1">
              {formatInteger(total)}
            </div>
          </div>
        </div>
      </div>

      {/* Show inline loader during subsequent data fetches */}
      {loading && !initialLoad && (
        <div className="flex justify-center my-8">
          <Loader />
        </div>
      )}

      {error && <div className="error mb-4">{error}</div>}

      <div className="flex justify-end mb-4">
        <Button
          onClick={handleExportCSV}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          Export Top 20 CSV
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="table-default">
          <thead>
            <tr>
              <th>Merchant Name</th>
              <th className="max-w-[120px]">Total Assigned Cashback</th>
              <th className="max-w-[120px]">Total Transaction Amount</th>
              <th className="max-w-[120px]">Transaction Count</th>
            </tr>
          </thead>
          <tbody>
            {merchants.map((merchant, index) => {
              // Calculate global index across pages
              const globalIndex = (pageNumber - 1) * PAGE_SIZE + index;
              
              // Determine highlighting class based on position
              let rowClass = "";
              if (globalIndex < 10) {
                rowClass = "bg-yellow-50 border-l-4 border-yellow-400"; // First 10 - yellow highlight
              } else if (globalIndex < 20) {
                rowClass = "bg-blue-50 border-l-4 border-blue-400"; // Second 10 - blue highlight
              }
              
              return (
                <tr key={index} className={rowClass}>
                  <td className="font-medium">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">#{globalIndex + 1}</span>
                      {merchant.merchantName}
                      {globalIndex < 10 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Top 10
                        </span>
                      )}
                      {globalIndex >= 10 && globalIndex < 20 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Top 20
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="max-w-[120px]">
                    {formatCurrency(merchant.totalAssignedCashback)}
                  </td>
                  <td className="max-w-[120px]">
                    {formatCurrency(merchant.totalTransactionAmount)}
                  </td>
                  <td className="max-w-[120px]">
                    {formatInteger(merchant.transactionCount)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center mt-4">
        <Button
          onClick={handlePreviousPage}
          disabled={pageNumber === 1}
          variant="outline"
        >
          Previous
        </Button>
        <span>
          Page {pageNumber} of {totalPages}
        </span>
        <Button
          onClick={handleNextPage}
          disabled={pageNumber >= totalPages}
          variant="outline"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default AdminTopMerchants;
